/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description store for claims requests
 */
// TODO
import { create } from 'zustand';
import _ from 'lodash';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';

import { covertDateToStringArray, DATE_PRESETS } from 'utils/dateParser';
import { dateType, EXPORT_OPTIONS } from 'utils/eligibility/eligibilityRequests';
import { DEFAULT_PAGINATION } from '@veritable/common/constants';

const defaultExportMetaData = {
	visible: false,
	dateRange: { key: dateType.radio, range: EXPORT_OPTIONS[0].value },
};

const defaultBatchResubmitMetaData = {
	visible: false,
	dateRange: { key: dateType.radio, range: EXPORT_OPTIONS[0].value },
	newDateRange: covertDateToStringArray(DATE_PRESETS.today.value),
};

const DEFAULT_SEARCH_PARAMS: ClaimStatusRequests.ISearchParams = {
	type: 'search',
	filterParams: null,
};

const initialState: ClaimStatusRequests.IValues = {
	...DEFAULT_PAGINATION,
	collapsed: true,
	searchParams: DEFAULT_SEARCH_PARAMS,
	apiParams: {},
	sortColumn: null,
	exportMetaData: defaultExportMetaData,
	batchResubmitMetaData: defaultBatchResubmitMetaData,
	selectedClaimRows: [],
};

const useClaimStatusRequestsStore = create<ClaimStatusRequests.IStore>()(
	persist(
		devtools(
			(set) => ({
				...initialState,
				actions: {
					resetStates: () =>
						set(() => ({
							collapsed: true,
							searchTerm: '',
						})),

					setCollapsed: (collapsed) => set(() => ({ collapsed })),
					setSearchParams: (searchParams) => set(() => ({ searchParams })),
					setPageSize: (pageSize) => set(() => ({ pageSize })),
					setPageNumber: (pageNumber) => set(() => ({ pageNumber })),
					setApiParams: (apiParams) => {
						set(() => ({
							apiParams: _.isEmpty(apiParams?.criteria) ? null : apiParams,
							pageNumber: DEFAULT_PAGINATION.pageNumber,
						}));
					},
					setSortColumn: (sortColumn) => {
						set((state) => ({
							sortColumn,
							apiParams: {
								...state.apiParams,
								sortBy: sortColumn
									? [{ column: sortColumn?.apikey as ClaimStatusRequests.ISortColumn, asc: sortColumn?.asc }]
									: null,
							},
						}));
					},

					setExportMetaData: (exportMetaData) => set(() => ({ exportMetaData })),
					setBatchResubmitMetaData: (batchResubmitMetaData) => set(() => ({ batchResubmitMetaData })),
					closeExportModal: () => set(() => ({ exportMetaData: defaultExportMetaData })),
					closeBatchResubmissionModal: () => set(() => ({ batchResubmitMetaData: defaultBatchResubmitMetaData })),
					setSelectedClaimRows: (selectedClaimRows) => set(() => ({ selectedClaimRows })),
				},
			}),
			{ name: 'Claims Requests' },
		),
		{
			name: 'claims-requests',
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({
				pageSize: state.pageSize,
				searchParams: state.searchParams,
				apiParams: state.apiParams,
				selectedClaimRows: state.selectedClaimRows,
				sortColumn: state.sortColumn,
			}),
		},
	),
);

// store to return actions and values
const useClaimStatusRequestsStoreValues = (): ClaimStatusRequests.IValues =>
	useClaimStatusRequestsStore(useShallow((state) => _.omit(state, ['actions'])));
const useClaimStatusRequestsStoreActions = (): ClaimStatusRequests.IActions =>
	useClaimStatusRequestsStore(useShallow((state) => state.actions));

export {
	DEFAULT_SEARCH_PARAMS,
	DEFAULT_PAGINATION as defaultPagination,
	useClaimStatusRequestsStoreValues,
	useClaimStatusRequestsStoreActions,
};
export default useClaimStatusRequestsStore;
